/**
 * emergencyTrips.js — R21 · PROD-164 (Dr. Rami 2026-10-03; Alex Q46, 2026-10-05), patient P1 step 2.
 * The 911 trips the symptom router answered BEFORE a case existed, carried into case creation.
 *
 * ★★★ THE GAP. POST /agent/triage runs before there is a case, so the server has nowhere to
 *   write a trip. Alex Q46: the patient app carries the trip into case creation; a trip that
 *   never becomes a case leaves only the server's anonymous counter line.
 *
 * ★★ THE CONTRACT (orella-api D5b step 3, 45060a1):
 *   - agent_triage answers a tripped message with `emergencyTrip` = exactly
 *     { language, route: 'triage', at } (never the patient's text, never the phrase);
 *   - create_case accepts `emergencyTrips` = a list of those, at most 20, each EXACTLY those
 *     three keys, language one of the 11 app languages, route 'triage', at ISO-8601 with an
 *     offset (validate_emergency_trips). A malformed list is refused whole (the case is still
 *     created), so only events of that exact shape are kept here.
 *   - chat trips are written by the server itself (agent_chat has the case id); not here.
 *
 * ★ WHERE THEY WAIT: sessionStorage (this tab, until case creation or the tab closes). No
 *   patient text is ever stored: the event has none. The newest 20 are kept.
 * ★ Every storage access is guarded: a WKWebView that refuses storage loses the carry, never
 *   the triage answer or the case.
 *
 * scripts/check-emergency-trips.mjs executes it.
 */
export const EMERGENCY_TRIPS_KEY = 'orella_pending_emergency_trips'
export const EMERGENCY_TRIPS_MAX = 20
export const EMERGENCY_LANGUAGES = Object.freeze(['ar', 'en', 'es', 'fr', 'hi', 'ko', 'pt', 'ru', 'tl', 'vi', 'zh'])

const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/

/** The event in the exact server shape, or null. Any other key (a text) makes it not an event. */
export function tripEvent(e) {
  if (!e || typeof e !== 'object' || Array.isArray(e)) return null
  if (Object.keys(e).sort().join() !== 'at,language,route') return null
  const { language, route, at } = e
  if (!EMERGENCY_LANGUAGES.includes(language) || route !== 'triage') return null
  if (typeof at !== 'string' || at.length < 10 || at.length > 40 || !ISO_WITH_OFFSET.test(at)) return null
  return { language, route: 'triage', at }
}

const store = (s) => {
  if (s) return s
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null } catch { return null }
}

/** The trips waiting for case creation, oldest first (each in the exact server shape). */
export function pendingEmergencyTrips(storage) {
  const s = store(storage)
  if (!s) return []
  try {
    const raw = JSON.parse(s.getItem(EMERGENCY_TRIPS_KEY) || '[]')
    return (Array.isArray(raw) ? raw : []).map(tripEvent).filter(Boolean).slice(-EMERGENCY_TRIPS_MAX)
  } catch { return [] }
}

/** Keep one triage answer's `emergencyTrip` for the next case creation. Returns the list. */
export function rememberEmergencyTrip(event, storage) {
  const ev = tripEvent(event)
  const s = store(storage)
  if (!ev || !s) return pendingEmergencyTrips(s)
  const next = [...pendingEmergencyTrips(s), ev].slice(-EMERGENCY_TRIPS_MAX)
  try { s.setItem(EMERGENCY_TRIPS_KEY, JSON.stringify(next)) } catch { /* storage refused */ }
  return next
}

/** After a case was created on the server: the trips went with it. */
export function clearEmergencyTrips(storage) {
  const s = store(storage)
  try { if (s) s.removeItem(EMERGENCY_TRIPS_KEY) } catch { /* storage refused */ }
}
